import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { KeycloakService } from 'keycloak-angular';
import { firstValueFrom, timeout } from 'rxjs';
import { UserPreferencesService } from './user-preferences';
import { LATAM_COUNTRY_CONFIGS } from '../core/country/latam-country-configs';

export const DEFAULT_LANG = 'en';
export const UNKNOWN_COUNTRY = 'N/A';

export const SUPPORTED_LANGS = [
  'de',
  'en',
  'es',
  'fr',
  'hu',
  'it',
  'pt-BR',
  'pt-PT',
  'sv',
] as const;

export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

export interface DetectedLocale {
  lang: SupportedLang;
  country: string | null;
}

const BROWSER_LANG_MAP: Readonly<Record<string, SupportedLang>> = {
  'pt-BR': 'pt-BR',
  'pt-PT': 'pt-PT',
  pt: 'pt-BR',
  en: 'en',
  es: 'es',
  fr: 'fr',
  de: 'de',
  hu: 'hu',
  it: 'it',
  sv: 'sv',
};

// Only locales that map unambiguously to a single country
const LOCALE_TO_COUNTRY: Readonly<Record<string, string>> = (() => {
  const counts = new Map<string, number>();
  for (const c of LATAM_COUNTRY_CONFIGS) counts.set(c.locale, (counts.get(c.locale) ?? 0) + 1);
  const map: Record<string, string> = {};
  for (const c of LATAM_COUNTRY_CONFIGS) {
    if (counts.get(c.locale) === 1) map[c.locale] = c.code;
  }
  return map;
})();

@Injectable({ providedIn: 'root' })
export class LanguageService {
  constructor(
    private readonly translate: TranslateService,
    private readonly keycloak: KeycloakService,
    private readonly userPreferences: UserPreferencesService,
  ) { }

  // Resolution chain: API UserPreferences → RHSSO → Browser → 'en'
  async detect(): Promise<DetectedLocale> {

    // Proteção para SSR: verifica de forma segura se 'window' existe no ambiente atual
    const isFlutter = typeof globalThis.window !== 'undefined' &&
      (globalThis.window as any).isFlutterWebView === true;

    // SE ESTÁ NO FLUTTER: usar token do Flutter
    if (isFlutter) {
      return (
        (await this.fromFlutterToken()) ??
        this.fromBrowser() ??
        { lang: DEFAULT_LANG, country: UNKNOWN_COUNTRY }
      );
    }

    return (
      (await this.fromUserPreferences()) ??
      (await this.fromKeycloak()) ??
      this.fromBrowser() ??
      { lang: DEFAULT_LANG, country: UNKNOWN_COUNTRY }
    );
  }

  private async fromFlutterToken(): Promise<DetectedLocale | null> {
    // Verificar se está no navegador
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
      console.warn('Não está no navegador');
      return null;
    }

    try {
      //  Obter token do Flutter
      const flutterToken =
        sessionStorage.getItem('flutter_token') ||
        sessionStorage.getItem('auth_token') ||
        localStorage.getItem('flutter_token');

      if (!flutterToken) {
        return null;
      }

      // Decodificar token
      const parts = flutterToken.split('.');
      if (parts.length <= 1) {
        console.warn('Token Flutter inválido');
        return null;
      }

      const base64Url = parts[1];
      let base64 = base64Url.replaceAll('-', '+').replaceAll('_', '/');
      while (base64.length % 4) {
        base64 += '=';
      }

      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.codePointAt(0)!.toString(16)).slice(-2))
          .join('')
      );

      const payload = JSON.parse(jsonPayload);

      const locale = payload?.locale || payload?.preferred_language || null;
      const normalized = locale ? locale.replace('_', '-') : null;

      // Usar o mesmo mapeamento de linguagem que fromUserPreferences
      const lang = normalized
        ? (BROWSER_LANG_MAP[normalized] ?? BROWSER_LANG_MAP[normalized.split('-')[0]] ?? DEFAULT_LANG)
        : DEFAULT_LANG;

      //  Extrair o alpha2 (2 letras) e converter usando LATAM_COUNTRY_CONFIGS
      let alpha2 = payload?.country || null;

      if (!alpha2 && normalized?.includes('-')) {
        alpha2 = normalized.split('-')[1].toUpperCase();  // 'pt-BR' → 'BR'
      }

      //  Usar o MESMO método que fromUserPreferences: procurar em LATAM_COUNTRY_CONFIGS
      const country = alpha2
        ? (LATAM_COUNTRY_CONFIGS.find((c) => c.alpha2 === alpha2)?.code ?? null)
        : (LOCALE_TO_COUNTRY[normalized] ?? null);

      if (lang || country) {
        return { lang, country };
      }

      return null;
    } catch (e) {
      console.error('Erro ao decodificar token Flutter:', e);
      return null;
    }
  }

  private async fromUserPreferences(): Promise<DetectedLocale | null> {

    const isFlutter = typeof globalThis.window !== 'undefined' &&
      (globalThis.window as any).isFlutterWebView === true;

    if (isFlutter) {
      return null;
    }

    try {
      const userId = await this.getUserId();
      if (userId == null) return null;
      const prefs = await firstValueFrom(
        this.userPreferences.getUserPreferences(userId).pipe(timeout(3000)),
      );
      const locale = prefs?.locale;
      if (!locale) return null;
      const normalized = locale.replace('_', '-');
      const lang =
        BROWSER_LANG_MAP[normalized] ?? BROWSER_LANG_MAP[normalized.split('-')[0]] ?? null;
      if (!lang) return null;
      // idiomTag format: "PT_BR" → alpha2 "BR" → country code "BRA"
      const alpha2 = prefs?.idiomTag?.split('_')[1]?.toUpperCase() ?? null;
      const country = alpha2
        ? (LATAM_COUNTRY_CONFIGS.find((c) => c.alpha2 === alpha2)?.code ?? null)
        : (LOCALE_TO_COUNTRY[normalized] ?? null);
      return { lang, country };
    } catch {
      return null;
    }
  }

  private async fromKeycloak(): Promise<DetectedLocale | null> {
    try {
      const tokenParsed = this.keycloak.getKeycloakInstance().tokenParsed;
      const locale = tokenParsed?.['locale'] as string | undefined;
      if (!locale) return null;
      const normalized = locale.replace('_', '-');
      const lang =
        BROWSER_LANG_MAP[normalized] ?? BROWSER_LANG_MAP[normalized.split('-')[0]] ?? null;
      if (!lang) return null;
      return { lang, country: LOCALE_TO_COUNTRY[normalized] ?? null };
    } catch {
      return null;
    }
  }

  private fromBrowser(): DetectedLocale {

    if (typeof globalThis.navigator === 'undefined') {
      return { lang: DEFAULT_LANG, country: UNKNOWN_COUNTRY };
    }

    const langs = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const lang of langs) {
      const mapped = BROWSER_LANG_MAP[lang] ?? BROWSER_LANG_MAP[lang.split('-')[0]];
      if (mapped) return { lang: mapped, country: UNKNOWN_COUNTRY };
    }
    return { lang: DEFAULT_LANG, country: UNKNOWN_COUNTRY };
  }

  private async getUserId(): Promise<number | null> {
    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) return null;
      const raw = this.keycloak.getKeycloakInstance().tokenParsed?.['usuoid'];
      const id = Number(raw);
      return Number.isFinite(id) && id > 0 ? id : null;
    } catch {
      return null;
    }
  }
}
